import React from 'react'

// Renderiza texto com a formatação do WhatsApp:
//   *negrito*  _itálico_  ~tachado~  ```mono```  `inline`
//   > citação (linha inteira)
//   - item / * item / 1. item (lista, linha inteira)
// Referência: https://faq.whatsapp.com/539178204879377/
//
// Casa um marcador por vez na ordem de prioridade. Para um texto como
// "*Nome:* Edvane" gera [<strong>Nome:</strong>, " Edvane"].
export function formatWhatsAppText(text: string): React.ReactNode {
  if (!text) return text

  // Quebra em linhas pra suportar `>` citação e listas
  const lines = text.split('\n')
  return lines.map((line, lineIdx) => {
    const key = `l${lineIdx}`
    // Citação: linha começa com "> "
    if (/^>\s+/.test(line)) {
      return (
        <React.Fragment key={key}>
          <blockquote className="border-l-2 border-current/40 pl-2 my-0.5 opacity-80">
            {renderInline(line.replace(/^>\s+/, ''))}
          </blockquote>
          {lineIdx < lines.length - 1 ? '' : null}
        </React.Fragment>
      )
    }
    // Item de lista (não-numerado): "- foo" ou "* foo"
    const bulletMatch = line.match(/^[\-*]\s+(.+)$/)
    if (bulletMatch) {
      return (
        <React.Fragment key={key}>
          <span className="inline-block">• {renderInline(bulletMatch[1])}</span>
          {lineIdx < lines.length - 1 && <br />}
        </React.Fragment>
      )
    }
    // Item de lista numerada: "1. foo" / "2. foo"
    const numMatch = line.match(/^(\d+)\.\s+(.+)$/)
    if (numMatch) {
      return (
        <React.Fragment key={key}>
          <span className="inline-block">{numMatch[1]}. {renderInline(numMatch[2])}</span>
          {lineIdx < lines.length - 1 && <br />}
        </React.Fragment>
      )
    }
    return (
      <React.Fragment key={key}>
        {renderInline(line)}
        {lineIdx < lines.length - 1 && <br />}
      </React.Fragment>
    )
  })
}

// Renderiza apenas os marcadores inline (negrito, itálico, tachado, mono).
function renderInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = []
  // Ordem: mono triplo > mono inline > negrito > itálico > tachado.
  // `[^*]+` (ou equivalente) impede que um marcador absorva texto demais.
  const regex = /(```[\s\S]+?```)|(`[^`\n]+`)|(\*[^*\n]+\*)|(_[^_\n]+_)|(~[^~\n]+~)/g
  let lastIndex = 0
  let m: RegExpExecArray | null
  let idx = 0
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index))
    const tok = m[0]
    const k = `t${idx++}`
    if (tok.startsWith('```')) {
      parts.push(<code key={k} className="bg-black/15 dark:bg-white/10 px-1.5 py-0.5 rounded text-xs font-mono">{tok.slice(3, -3)}</code>)
    } else if (tok.startsWith('`')) {
      parts.push(<code key={k} className="bg-black/15 dark:bg-white/10 px-1 rounded text-[0.9em] font-mono">{tok.slice(1, -1)}</code>)
    } else if (tok.startsWith('*')) {
      parts.push(<strong key={k}>{tok.slice(1, -1)}</strong>)
    } else if (tok.startsWith('_')) {
      parts.push(<em key={k}>{tok.slice(1, -1)}</em>)
    } else if (tok.startsWith('~')) {
      parts.push(<s key={k}>{tok.slice(1, -1)}</s>)
    }
    lastIndex = m.index + tok.length
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex))
  return parts.length > 0 ? parts : [text]
}
